import { type LicenseLogo } from "@/lib/getLicenseLogos"
import {
  buildLogoBarLayout,
  GAP,
  ICON_SIZE,
  resolveLogoVariant,
  type LayoutCell
} from "@/lib/logoBarLayout"
import { fetchLogoRaw } from "@/lib/licenseLogos"

export const BASE_WIDGET_DPI = 96
export const DEFAULT_WIDGET_PNG_DPI = 192
export const DEFAULT_WIDGET_MAX_WIDTH = 492
export const DEFAULT_WIDGET_PADDING = 16
export const DEFAULT_WIDGET_TITLE_GAP = 8
export const DEFAULT_WIDGET_TITLE_SIZE = 14
export const DEFAULT_WIDGET_TITLE_LINE_HEIGHT = 1.25
export const DEFAULT_WIDGET_RADIUS = 16
export const DEFAULT_WIDGET_BG = "#f3f4f6"
export const DEFAULT_WIDGET_TITLE_COLOR = "#334155"
export const DEFAULT_WIDGET_DIVIDER_COLOR = "#cbd5e1"

const DIVIDER_PADDING = 6
const DIVIDER_LINE = 1
const DIVIDER_SPACE = DIVIDER_LINE + DIVIDER_PADDING * 2
const SMALL_ICON_SIZE = ICON_SIZE / 2

export type WidgetLogoImageOptions = {
  dpi?: number
  maxWidth?: number
  padding?: number
  titleGap?: number
  titleFontSize?: number
  titleLineHeight?: number
  cornerRadius?: number
  backgroundColor?: string
  titleColor?: string
  dividerColor?: string
}

export type WidgetLogoSvgResult = {
  svg: string
  width: number
  height: number
  scale: number
}

const escapeXml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;")

const encodeSvgDataUrl = (svg: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)
    .replace(/'/g, "%27")
    .replace(/"/g, "%22")}`

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.decoding = "async"
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error("Failed to load SVG image"))
    img.src = src
  })

const buildLogoSvgMap = async (logos: LicenseLogo[]) => {
  const pending = new Map<string, Promise<string>>()
  logos.forEach(logo => {
    const variant = resolveLogoVariant(logo)
    const key = `${variant}:${logo.key}`
    if (!pending.has(key)) {
      pending.set(key, fetchLogoRaw(logo.key, variant))
    }
  })

  const resolved = new Map<string, string>()
  await Promise.all(
    Array.from(pending.entries()).map(async ([key, promise]) => {
      const svg = await promise
      if (svg) resolved.set(key, svg)
    })
  )
  return resolved
}

const getGroupPositions = (count: number) => {
  if (count <= 1) {
    return [{ x: (ICON_SIZE - SMALL_ICON_SIZE) / 2, y: 0 }]
  }
  if (count === 2) {
    return [
      { x: 0, y: 0 },
      { x: SMALL_ICON_SIZE, y: 0 }
    ]
  }
  if (count === 3) {
    return [
      { x: 0, y: 0 },
      { x: SMALL_ICON_SIZE, y: 0 },
      { x: (ICON_SIZE - SMALL_ICON_SIZE) / 2, y: SMALL_ICON_SIZE }
    ]
  }

  return Array.from({ length: count }, (_, index) => ({
    x: (index % 2) * SMALL_ICON_SIZE,
    y: Math.floor(index / 2) * SMALL_ICON_SIZE
  }))
}

const renderLogoCell = (
  cell: LayoutCell,
  logoSvgs: Map<string, string>,
  hasDivider: boolean,
  sharingWidth: number,
  sharingCellCount: number
) => {
  const parts: string[] = []
  const baseY = cell.row * (ICON_SIZE + GAP)
  const dividerOffset = hasDivider && cell.col >= sharingWidth ? DIVIDER_SPACE : 0
  let baseX = cell.col * (ICON_SIZE + GAP) + dividerOffset

  const shouldCenterSharing =
    sharingCellCount === 3 && sharingWidth === 2 && cell.col < sharingWidth && cell.row === 1

  if (shouldCenterSharing) {
    baseX =
      (sharingWidth * ICON_SIZE + Math.max(0, sharingWidth - 1) * GAP - ICON_SIZE) /
      2
  }

  if (!cell.isGroup) {
    const logo = cell.logos[0]
    const variant = resolveLogoVariant(logo)
    const svg = logoSvgs.get(`${variant}:${logo.key}`)
    if (!svg) return parts
    parts.push(
      `<image x="${baseX}" y="${baseY}" width="${ICON_SIZE}" height="${ICON_SIZE}" href="${encodeSvgDataUrl(
        svg
      )}" />`
    )
    return parts
  }

  const positions = getGroupPositions(cell.logos.length)
  cell.logos.forEach((logo, index) => {
    const position = positions[index]
    if (!position) return
    const variant = resolveLogoVariant(logo)
    const svg = logoSvgs.get(`${variant}:${logo.key}`)
    if (!svg) return
    parts.push(
      `<image x="${baseX + position.x}" y="${baseY + position.y}" width="${SMALL_ICON_SIZE}" height="${SMALL_ICON_SIZE}" href="${encodeSvgDataUrl(
        svg
      )}" />`
    )
  })

  return parts
}

export const buildWidgetLogoSvg = async ({
  logos,
  licenseName,
  options = {}
}: {
  logos: LicenseLogo[]
  licenseName: string
  options?: WidgetLogoImageOptions
}): Promise<WidgetLogoSvgResult> => {
  const layout = buildLogoBarLayout(logos)
  const hasDivider = layout.sharingWidth > 0 && layout.columns > layout.sharingWidth
  const dividerSpace = hasDivider ? DIVIDER_SPACE : 0
  const gridWidth =
    layout.columns * ICON_SIZE + Math.max(0, layout.columns - 1) * GAP + dividerSpace
  const gridHeight = layout.rows * ICON_SIZE + Math.max(0, layout.rows - 1) * GAP

  const maxWidth = options.maxWidth ?? DEFAULT_WIDGET_MAX_WIDTH
  const padding = options.padding ?? DEFAULT_WIDGET_PADDING
  const titleGap = options.titleGap ?? DEFAULT_WIDGET_TITLE_GAP
  const titleFontSize = options.titleFontSize ?? DEFAULT_WIDGET_TITLE_SIZE
  const titleLineHeight = options.titleLineHeight ?? DEFAULT_WIDGET_TITLE_LINE_HEIGHT
  const titleHeight = titleFontSize * titleLineHeight
  const cornerRadius = options.cornerRadius ?? DEFAULT_WIDGET_RADIUS
  const backgroundColor = options.backgroundColor ?? DEFAULT_WIDGET_BG
  const titleColor = options.titleColor ?? DEFAULT_WIDGET_TITLE_COLOR
  const dividerColor = options.dividerColor ?? DEFAULT_WIDGET_DIVIDER_COLOR

  const innerWidth = Math.max(0, maxWidth - padding * 2)
  const scale = gridWidth > 0 ? Math.min(1, innerWidth / gridWidth) : 1
  const scaledGridHeight = gridHeight * scale
  const width = maxWidth
  const height = padding + titleHeight + titleGap + scaledGridHeight + padding
  const gridY = padding + titleHeight + titleGap

  const sharingCellCount = layout.cells.filter(
    cell => !cell.isGroup && cell.col < layout.sharingWidth
  ).length

  const logoSvgs = await buildLogoSvgMap(logos)

  const parts: string[] = []
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`
  )
  parts.push(
    `<rect x="0" y="0" width="${width}" height="${height}" rx="${cornerRadius}" ry="${cornerRadius}" fill="${backgroundColor}" />`
  )
  parts.push(
    `<text x="${padding}" y="${padding}" font-family="Inter, sans-serif" font-size="${titleFontSize}" font-weight="600" fill="${titleColor}" dominant-baseline="hanging">${escapeXml(
      licenseName
    )}</text>`
  )
  parts.push(`<g transform="translate(${padding}, ${gridY}) scale(${scale})">`)
  if (hasDivider) {
    const dividerOffset =
      layout.sharingWidth * ICON_SIZE +
      Math.max(0, layout.sharingWidth - 1) * GAP +
      DIVIDER_PADDING
    parts.push(
      `<rect x="${dividerOffset}" y="0" width="${DIVIDER_LINE}" height="${gridHeight}" fill="${dividerColor}" />`
    )
  }
  layout.cells.forEach(cell => {
    parts.push(
      ...renderLogoCell(cell, logoSvgs, hasDivider, layout.sharingWidth, sharingCellCount)
    )
  })
  parts.push("</g>")
  parts.push("</svg>")

  return { svg: parts.join(""), width, height, scale }
}

export const buildWidgetLogoPng = async (
  svgResult: WidgetLogoSvgResult,
  options: WidgetLogoImageOptions = {}
) => {
  const dpi = options.dpi ?? DEFAULT_WIDGET_PNG_DPI
  const scale = dpi / BASE_WIDGET_DPI
  const width = Math.max(1, Math.round(svgResult.width * scale))
  const height = Math.max(1, Math.round(svgResult.height * scale))
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Canvas context unavailable")

  const img = await loadImage(encodeSvgDataUrl(svgResult.svg))
  ctx.setTransform(scale, 0, 0, scale, 0, 0)
  ctx.drawImage(img, 0, 0)

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        reject(new Error("Failed to render widget PNG"))
        return
      }
      resolve(blob)
    }, "image/png")
  })
}
